import React from 'react';

const Features: React.FC = () => {
  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Human-Like Conversations",
      description: "Advanced neural networks that understand context, emotion, and nuance for truly natural interactions that customers can't distinguish from human agents."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Lightning Response",
      description: "Sub-100ms response times powered by edge computing and optimized AI models for instant customer satisfaction and seamless conversations."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      title: "Enterprise Security",
      description: "Military-grade encryption, SOC 2 Type II compliance, and GDPR/HIPAA adherence with zero-trust architecture and end-to-end protection."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Real-Time Analytics",
      description: "Advanced AI-powered insights with predictive analytics, sentiment analysis, and performance optimization to maximize customer satisfaction."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
        </svg>
      ),
      title: "Global Scale",
      description: "Support for 100+ languages with native-level fluency, cultural understanding, and regional compliance across all major markets worldwide."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1V9a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V4a1 1 0 011-1h3a1 1 0 011 1v1a2 2 0 104 0V4z" />
        </svg>
      ),
      title: "Seamless Integration",
      description: "One-click integration with 500+ platforms including Salesforce, HubSpot, Zendesk, and custom APIs with real-time synchronization."
    }
  ];

  return (
    <section id="features" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 section-dark relative overflow-hidden">
      {/* Background Effects - Corporate Colors */}
      <div className="floating-orb w-64 sm:w-80 lg:w-96 h-64 sm:h-80 lg:h-96 bg-blue-600 top-20 right-10 sm:right-20"></div>
      <div className="floating-orb w-48 sm:w-56 lg:w-64 h-48 sm:h-56 lg:h-64 bg-slate-700 bottom-40 left-10 sm:left-20"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-full glass-effect text-white text-xs sm:text-sm font-medium mb-6 sm:mb-8">
            <span className="gradient-text font-semibold">Enterprise-Grade Technology</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white mb-6 sm:mb-8 leading-tight px-4">
            Professional-Grade{' '}
            <span className="gradient-text">AI Capabilities</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed px-4">
            Experience enterprise AI that delivers reliable, scalable customer service solutions 
            with the professionalism and consistency your business demands.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group glass-card p-6 sm:p-8 rounded-2xl sm:rounded-3xl card-hover relative overflow-hidden"
            >
              {/* Gradient Border Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl sm:rounded-2xl flex items-center justify-center text-white mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-500 relative">
                  <div className="w-6 h-6 sm:w-8 sm:h-8">
                    {feature.icon}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl sm:rounded-2xl opacity-0 group-hover:opacity-20 animate-pulse"></div>
                </div>
                
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4 group-hover:text-glow transition-all duration-300">
                  {feature.title}
                </h3>
                
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Performance Metrics */}
        <div className="mt-16 sm:mt-24 lg:mt-32 glass-card rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
          
          <div className="relative z-10 text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-3 sm:mb-4">
              Performance That <span className="gradient-text">Speaks Volumes</span>
            </h3>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300">
              Real metrics from real deployments across enterprise customers
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-black gradient-text mb-1 sm:mb-2">99.97%</div>
              <div className="text-xs sm:text-sm lg:text-base text-gray-400 font-medium">Accuracy Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-black gradient-text mb-1 sm:mb-2">&lt;100ms</div>
              <div className="text-xs sm:text-sm lg:text-base text-gray-400 font-medium">Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-black gradient-text mb-1 sm:mb-2">4.9/5</div>
              <div className="text-xs sm:text-sm lg:text-base text-gray-400 font-medium">Customer Rating</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-black gradient-text mb-1 sm:mb-2">100+</div>
              <div className="text-xs sm:text-sm lg:text-base text-gray-400 font-medium">Languages</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12 sm:mt-16 lg:mt-20">
          <button className="btn-primary text-base sm:text-lg font-semibold px-6 sm:px-8 py-3 sm:py-4">
            Explore All Capabilities
          </button>
        </div>
      </div>
    </section>
  );
};

export default Features; 